import React from 'react';
import { universalToFdi } from '../../case-dentist-assistant/fdiToothMapping';

const VAL = {
  Improve: 'Améliorer',
  Maintain: 'Maintenir',
  Correct: 'Corriger',
  'Close All': 'Fermer tout',
  Create: 'Créer',
  Yes: 'Oui',
  No: 'Non',
  Primarily: 'Principalement',
  'As Needed': 'Si nécessaire',
  None: 'Aucun',
};

function disp(v) {
  if (v == null || v === '') return '—';
  return VAL[v] ?? v;
}

function dispArch(arch) {
  if (!arch) return '—';
  const a = String(arch).toLowerCase();
  if (a === 'upper') return 'Arcade supérieure';
  if (a === 'lower') return 'Arcade inférieure';
  if (a === 'both') return 'Supérieure et inférieure';
  return arch;
}

function dispTreatment(t) {
  if (!t) return '—';
  const m = {
    'Full Arch': 'Arcade complète',
    Partial: 'Traitement partiel',
    Comprehensive: 'Traitement complet',
    Anterior: 'Antérieur uniquement',
  };
  return m[t] ?? t;
}

function teethFdiList(universalArr) {
  if (!universalArr?.length) return '—';
  const fdis = [...universalArr].map((u) => universalToFdi(u)).filter(Boolean).sort((a, b) => a - b);
  return fdis.length ? fdis.join(', ') : '—';
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2 border-b border-border/60 last:border-0">
      <span className="text-xs text-muted-foreground sm:w-52 shrink-0">{label}</span>
      <span className="text-sm font-medium text-foreground">{children}</span>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="rounded-xl border border-border/80 bg-card p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
      <div className="divide-y divide-border/40">{children}</div>
    </div>
  );
}

/**
 * Affichage lecture seule de la prescription (cases + case_treatment_specs).
 */
const CasePrescriptionReadView = ({ caseData }) => {
  const specs = caseData?.treatmentSpecs;

  const expand = specs?.expand || {};
  const procline = specs?.procline || {};
  const canine = specs?.canine || {};
  const molar = specs?.molar || {};
  const iprA = specs?.ipr_anterior || {};
  const iprR = specs?.ipr_posterior_right || {};
  const iprL = specs?.ipr_posterior_left || {};

  const notes = caseData?.questionsNotes ?? caseData?.questions_notes ?? '';

  if (!caseData) {
    return <p className="text-sm text-muted-foreground">Chargement…</p>;
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-sm text-muted-foreground">
        Données issues de la prescription enregistrée pour ce dossier.
      </p>

      <Block title="Prescription d’alignement">
        <Row label="Arcade à traiter">{dispArch(caseData.archToTreat || caseData.arch_to_treat)}</Row>
        <Row label="Type de traitement">{dispTreatment(caseData.treatmentType || caseData.treatment_type)}</Row>
        <Row label="Overjet">{disp(specs?.overject)}</Row>
        <Row label="Overbite">{disp(specs?.overbite)}</Row>
        <Row label="Ligne médiane">{disp(specs?.midline)}</Row>
        <Row label="Occlusion croisée postérieure">{disp(specs?.posterior_crossbite)}</Row>
        <Row label="Espacement">{disp(specs?.spacing)}</Row>
        <Row label="Distalisation séquentielle">{disp(specs?.sequential_distalization)}</Row>
      </Block>

      <Block title="Encombrement & espacement">
        <Row label="Expansion — haut">{disp(expand.upper)}</Row>
        <Row label="Expansion — bas">{disp(expand.lower)}</Row>
        <Row label="Proclination — haut">{disp(procline.upper)}</Row>
        <Row label="Proclination — bas">{disp(procline.lower)}</Row>
      </Block>

      <Block title="Relation antéro-postérieure">
        <Row label="Canine — haut">{disp(canine.upper)}</Row>
        <Row label="Canine — bas">{disp(canine.lower)}</Row>
        <Row label="Molaire — haut">{disp(molar.upper)}</Row>
        <Row label="Molaire — bas">{disp(molar.lower)}</Row>
      </Block>

      <Block title="Réduction inter-proximale (IPR)">
        <Row label="IPR antérieur — haut">{disp(iprA.upper)}</Row>
        <Row label="IPR antérieur — bas">{disp(iprA.lower)}</Row>
        <Row label="IPR post. droit — haut">{disp(iprR.upper)}</Row>
        <Row label="IPR post. droit — bas">{disp(iprR.lower)}</Row>
        <Row label="IPR post. gauche — haut">{disp(iprL.upper)}</Row>
        <Row label="IPR post. gauche — bas">{disp(iprL.lower)}</Row>
      </Block>

      <Block title="Sélections dentaires (notation ISO)">
        <Row label="Restrictions de mouvement">
          {teethFdiList(specs?.tooth_mvt_restrictions)}
        </Row>
        <Row label="Restrictions d’attachements">
          {teethFdiList(specs?.attachment_restrictions)}
        </Row>
        <Row label="Extractions">{teethFdiList(specs?.extractions)}</Row>
        <Row label="Dents manquantes">{teethFdiList(specs?.missing_teeth)}</Row>
      </Block>

      {notes ? (
        <div className="rounded-xl border border-border/80 bg-card p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-foreground mb-2">Notes supplémentaires</h3>
          <p className="text-sm text-foreground whitespace-pre-wrap">{notes}</p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Aucune note supplémentaire.</p>
      )}

      {!specs && !notes && (
        <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border p-6 text-center">
          Aucune prescription enregistrée pour l’instant.
        </p>
      )}
    </div>
  );
};

export default CasePrescriptionReadView;
